import { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
import { varinymAPI } from '../lib/varinym-api';
import { useAuth } from '../hooks/useAuth';
import type { PaddleTier, PaddleBillingCycle } from '../types';

const VALID_TIERS: PaddleTier[] = ['developer', 'starter', 'growth', 'professional', 'enterprise'];
const VALID_CYCLES: PaddleBillingCycle[] = ['monthly', 'annual'];

export function CheckoutInit() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [isRedirecting, setIsRedirecting] = useState(false);

  // Use ref to prevent double-invocation in React StrictMode
  const hasInitiatedCheckout = useRef(false);

  const tier = searchParams.get('tier') as PaddleTier | null;
  const intervalParam = searchParams.get('interval') as PaddleBillingCycle | null;
  const billing_cycle: PaddleBillingCycle =
    intervalParam && VALID_CYCLES.includes(intervalParam) ? intervalParam : 'monthly';

  useEffect(() => {
    // Prevent running the effect multiple times (handles React StrictMode double-invoke)
    if (hasInitiatedCheckout.current) {
      console.log('[CheckoutInit] Already initiated checkout, skipping');
      return;
    }

    if (!tier || !VALID_TIERS.includes(tier)) {
      setError(`Invalid or missing plan: "${tier ?? ''}". Return to the pricing page and try again.`);
      return;
    }
    if (!user?.email || !user?.subscription) {
      console.log('[CheckoutInit] Waiting for user data...', { email: user?.email, hasSubscription: !!user?.subscription });
      return; // wait for user data with subscription
    }

    console.log('[CheckoutInit] User data loaded, checking subscription status...', user.subscription);

    // Check if user already has an active paid subscription (anything except 'developer' tier)
    if (user.subscription.tier !== 'developer' && user.subscription.status === 'active') {
      console.log('[CheckoutInit] User already has active paid subscription:', user.subscription.tier);
      console.log('[CheckoutInit] Redirecting to dashboard');
      hasInitiatedCheckout.current = true; // Mark before redirect
      navigate('/', { replace: true });
      return;
    }

    // Mark that we're initiating checkout to prevent duplicate calls
    hasInitiatedCheckout.current = true;

    console.log('[CheckoutInit] Creating checkout session...', { tier, billing_cycle });
    setIsRedirecting(true);
    varinymAPI
      .createCheckoutSession(tier, billing_cycle, user.email)
      .then(({ checkout_url }) => {
        console.log('[CheckoutInit] Checkout session created, redirecting to:', checkout_url);
        window.location.href = checkout_url;
      })
      .catch((err: Error) => {
        console.error('[CheckoutInit] Failed to create checkout session:', err);
        hasInitiatedCheckout.current = false; // Reset on error so user can retry
        setIsRedirecting(false);
        setError(err.message || 'Failed to create checkout session. Please try again.');
      });
  }, [tier, billing_cycle, user?.email, user?.subscription, navigate]);

  // Show loading state while:
  // - Waiting for user data with subscription info
  // - No tier param (will show error after)
  // - Currently redirecting to Paddle
  if (!error && (!user?.email || !user?.subscription || !tier || isRedirecting)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto" />
          <p className="text-sm text-muted-foreground">Preparing your checkout…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4">
        <Card className="max-w-md w-full">
          <div className="space-y-4">
            <h2 className="text-lg font-semibold text-foreground">Checkout Error</h2>
            <p className="text-sm text-muted-foreground">{error}</p>
            <div className="flex gap-3">
              <Button onClick={() => window.history.back()} variant="secondary">Go Back</Button>
              <Button onClick={() => window.location.reload()}>Try Again</Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  // This should rarely/never be reached since we redirect before getting here
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center space-y-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto" />
        <p className="text-sm text-muted-foreground">Redirecting…</p>
      </div>
    </div>
  );
}
